"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatIsDirectPartMarking() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: diagramRef,
    isVisible: diagramVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const callouts = [{
    label: "LASER FREQUENCY 1064NM",
    top: "12%",
    left: "55%"
  }, {
    label: "DEPTH 0.05MM \u00B1 0.01",
    top: "42%",
    left: "58%"
  }, {
    label: "POWER 20W PULSED",
    top: "70%",
    left: "55%"
  }, {
    label: "SURFACE INTEGRITY",
    top: "85%",
    left: "20%"
  }];
  const stats = [{
    value: "99%",
    label: "PERMANENCE",
    descriptor: "Direct part marking creates indelible identifiers that withstand extreme industrial conditions."
  }, {
    value: "100%",
    label: "TRACEABILITY",
    descriptor: "Full lifecycle tracking from raw material procurement to final assembly and field service."
  }, {
    value: "0",
    label: "CONSUMABLES",
    descriptor: "Laser marking eliminates the need for inks, solvents, or physical labels in production."
  }, {
    value: "24/7",
    label: "AUTOMATION",
    descriptor: "High-speed integration with robotic cells for continuous, error-free part identification."
  }];
  return <section data-section-id="3634" id="what-is-direct-part-marking" className="relative bg-background text-foreground overflow-hidden py-32 md:py-40 lg:py-[200px] px-6 md:px-10 lg:px-16">
      {}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-30" style={{
      backgroundImage: "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-16 lg:gap-20 items-center">
          {}
          <div ref={diagramRef} data-index={0} className={`relative aspect-[3/4] w-full transition-all duration-1000 ease-out ${diagramVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <svg viewBox="0 0 400 540" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-label="Direct part marking laser system technical illustration">
              {}
              <rect x="120" y="500" width="160" height="20" stroke="currentColor" strokeWidth="1" />
              <line x1="100" y1="520" x2="300" y2="520" stroke="currentColor" strokeWidth="1" />

              {}
              <rect x="160" y="80" width="80" height="420" stroke="currentColor" strokeWidth="1" />
              <line x1="160" y1="80" x2="160" y2="500" stroke="currentColor" strokeWidth="1" />
              <line x1="240" y1="80" x2="240" y2="500" stroke="currentColor" strokeWidth="1" />

              {}
              {[120, 180, 240, 300, 360, 420].map((y, i) => <g key={i}>
                  <line x1="160" y1={y} x2="240" y2={y} stroke="currentColor" strokeWidth="0.5" />
                  {}
                  <path d={`M 145 ${y} q -8 -10 -15 -4 q 7 2 8 10 z`} stroke="currentColor" strokeWidth="1" fill="none" />
                  <path d={`M 145 ${y} q -12 -6 -18 2 q 10 0 12 8 z`} stroke="currentColor" strokeWidth="1" fill="none" />
                  {}
                  <path d={`M 255 ${y} q 8 -10 15 -4 q -7 2 -8 10 z`} stroke="currentColor" strokeWidth="1" fill="none" />
                  <path d={`M 255 ${y} q 12 -6 18 2 q -10 0 -12 8 z`} stroke="currentColor" strokeWidth="1" fill="none" />
                  {}
                  <circle cx="155" cy={y} r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
                  <circle cx="245" cy={y} r="3" stroke="currentColor" strokeWidth="0.5" fill="none" />
                </g>)}

              {}
              {[100, 160, 220, 280, 340, 400, 460].map((y, i) => <g key={`led-${i}`}>
                  <circle cx="200" cy={y} r="4" fill="var(--primary)" />
                  <circle cx="200" cy={y} r="8" stroke="var(--primary)" strokeWidth="0.5" fill="none" opacity="0.5" />
                </g>)}

              {}
              <path d="M 200 80 L 200 500" stroke="var(--primary)" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.9" />

              {}
              <rect x="140" y="50" width="120" height="30" stroke="currentColor" strokeWidth="1" />
              <line x1="140" y1="65" x2="260" y2="65" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" />

              {}
              <rect x="140" y="500" width="120" height="15" stroke="var(--primary)" strokeWidth="1" />

              {}
              <line x1="220" y1="100" x2="290" y2="60" stroke="currentColor" strokeWidth="0.5" />
              <circle cx="220" cy="100" r="2" fill="var(--primary)" />

              <line x1="220" y1="250" x2="300" y2="240" stroke="currentColor" strokeWidth="0.5" />
              <circle cx="220" cy="250" r="2" fill="var(--primary)" />

              <line x1="220" y1="400" x2="290" y2="395" stroke="currentColor" strokeWidth="0.5" />
              <circle cx="220" cy="400" r="2" fill="var(--primary)" />

              <line x1="180" y1="470" x2="100" y2="470" stroke="currentColor" strokeWidth="0.5" />
              <circle cx="180" cy="470" r="2" fill="var(--primary)" />
            </svg>

            {}
            {callouts.map((c, i) => <div key={i} data-index={i} className="absolute font-mono text-[10px] md:text-xs uppercase tracking-widest text-foreground/80 pointer-events-none" style={{
            top: c.top,
            left: c.left
          }}>
                <span className="inline-block border-l border-primary pl-2">{c.label}</span>
              </div>)}
          </div>

          {}
          <div>
            <div ref={headerRef} className={`transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground mb-8">
                03 / Technology
              </div>

              <h2 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-5xl md:text-6xl lg:text-[5rem]">
                <span className="block text-foreground">Direct Part Marking</span>
                <span className="block text-primary">Precision Identification</span>
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-10 max-w-xl">
                Direct Part Marking (DPM) is the process of permanently marking components with data-rich identifiers. 
                By utilizing advanced laser systems, we ensure that every part remains traceable throughout its 
                entire operational lifespan, meeting the rigorous quality standards required in aerospace, 
                automotive, and medical manufacturing sectors.
              </p>
            </div>

            {}
            <div ref={statsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mt-14">
              {stats.map((stat, i) => <div key={i} data-index={i} className={`group relative h-60 p-6 md:p-7 flex flex-col justify-between border border-border bg-card/40 backdrop-blur-md shadow-lg transition-all duration-700 ease-out hover:bg-card/60 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: statsVisible ? `${i * 120}ms` : "0ms"
            }}>
                  <div className="relative inline-block w-fit">
                    <div className="font-default font-bold text-primary text-6xl md:text-7xl leading-none transition-transform duration-300 ease-out group-hover:scale-105 origin-left">
                      {stat.value}
                    </div>
                    <span aria-hidden="true" className="absolute left-0 -bottom-2 h-px bg-primary w-0 transition-all duration-500 ease-out group-hover:w-full" />
                  </div>

                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-foreground mb-2">
                      {stat.label}
                    </div>
                    <p className="font-default text-sm text-muted-foreground leading-snug">
                      {stat.descriptor}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}